import {
  IAdminUser,
  IAdminUserInvitation
} from '@dadohr/shared/domainTypes/adminUser';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { SsoProviderType } from '@dadohr/shared/domainTypes/sso';
import { CF } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from '@material-ui/core';
import { truncate } from 'lodash';
import React, { useMemo, useState } from 'react';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../../../../components/AdditionalActionsMenu';
import { ButtonWithPromise } from '../../../../../../../components/ButtonWithPromise';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import {
  ID_TO_ROW_KEY,
  ItemSorters,
  RowsRenderer,
  RowsRendererScrollContainer,
  ROW_HEIGHTS
} from '../../../../../../../components/GroupableList';
import { PaperLoader } from '../../../../../../../components/Loader';
import { IColumn } from '../../../../../../../components/Table/VirtualizedSortable';
import { useDialogState } from '../../../../../../../hooks/useDialogState';
import { useSnackbar } from '../../../../../../../hooks/useSnackbar';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import { Section } from '../../../../../../../layout/Section';
import { useRoutes } from '../../../../../../../routes';
import { useAdminUserInvitationsInOrg } from '../../../../../../../services/adminUsers';
import {
  combineLoadingValues,
  useMappedLoadingValue
} from '../../../../../../../services/firebase';
import { callFirebaseFunction } from '../../../../../../../services/firebaseFunctions';
import { toFullName } from '../../../../../../../services/user';
import { useAdminUsersInOrg } from '../../../../../../services/adminUsers';
import { useAdminCurrentUser } from '../../../../../../services/auth';
import { getAppUrl } from '../../../../../../services/routes';

const HEIGHT = 200;

type ColumnName = 'id' | 'name' | 'email' | 'actions';
type D = Doc<IAdminUserInvitation>;
type OtherProps = { org: Doc<IOrganization> };
type Column = IColumn<D, ColumnName, OtherProps>;

const Actions = ({ d, o }: { d: D; o: OtherProps }) => {
  const { enqueueSnackbarSuccess } = useSnackbar();
  const { ROUTES } = useRoutes();
  const options: AdditionActionsMenuOption[] = useMemo(() => {
    return [
      {
        key: 'copyLink',
        label: 'Copy invitation link',
        onClick: () => {
          const url =
            o.org.data.sso.enabledForAdmins &&
            o.org.data.sso.provider === SsoProviderType.MS
              ? `${getAppUrl()}${ROUTES.base.url(o.org.id)}`
              : `${getAppUrl()}${ROUTES.acceptInvitation.url(d.id)}`;
          navigator.clipboard.writeText(url);
          enqueueSnackbarSuccess(
            `Invitation link copied to clipboard!\n${url}`
          );
        }
      }
    ];
  }, [d, enqueueSnackbarSuccess, ROUTES]);
  return <AdditionalActionsMenu options={options} />;
};

const COLUMNS: Column[] = [
  {
    key: 'id',
    head: () => 'ID',
    cell: (d) => <div>{truncate(d.id, { length: 12 })}</div>,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 0
  },
  {
    key: 'name',
    head: () => 'Name',
    cell: (d) => toFullName(d.data),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'email',
    head: () => 'Email',
    cell: (d) => d.data.email,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'actions',
    head: () => '',
    alternateHead: () => 'Actions',
    cell: (d, o) => <Actions d={d} o={o} />,
    sortable: false,
    align: 'right',
    width: 50,
    flexGrow: 0
  }
];

const SORTERS: ItemSorters<D, OtherProps> = {
  email: {
    key: 'email',
    items: { sort: (d) => d.data.email, dir: 'asc' }
  },
  name: {
    key: 'name',
    items: { sort: (d) => toFullName(d.data), dir: 'asc' }
  }
};

const DEFAULT_SORTER = SORTERS.email;

type InviteArgs = {
  email: string;
  firstName: string;
  lastName: string;
};

const inviteUser = async (
  inviterId: string,
  orgId: string,
  args: InviteArgs
) => {
  await callFirebaseFunction(CF.adminUser.createInvitation, {
    orgId,
    user: {
      firstName: args.firstName,
      preferredName: args.firstName,
      middleName: '',
      lastName: args.lastName,
      email: args.email
    },
    invitedBy: inviterId,
    triggerMail: true
  });
};

const INITIAL_INVITE_STATE: InviteArgs = {
  email: '',
  firstName: '',
  lastName: ''
};
const InviteAdminUserDialog = ({
  open,
  onClose,
  orgId,
  currentAdmins
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  currentAdmins: Doc<IAdminUser>[];
}) => {
  const [state, setState] = useState<InviteArgs>(INITIAL_INVITE_STATE);
  const currentUser = useAdminCurrentUser();
  const { enqueueSnackbarSuccess } = useSnackbar();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Invite admin user</DialogTitle>
      <DialogContent>
        <FormRow>
          <TextField
            label="Email"
            value={state.email}
            onChange={(ev) =>
              setState((s) => ({ ...s, email: ev.target.value }))
            }
            fullWidth
            variant="outlined"
          />
        </FormRow>
        <FormRow>
          <TextField
            label="First Name"
            value={state.firstName}
            onChange={(ev) =>
              setState((s) => ({ ...s, firstName: ev.target.value }))
            }
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Last Name"
            value={state.lastName}
            onChange={(ev) =>
              setState((s) => ({ ...s, lastName: ev.target.value }))
            }
            fullWidth
            variant="outlined"
          />
        </FormRow>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <ButtonWithPromise
          variant="contained"
          color="secondary"
          disabled={
            !state.email ||
            !state.firstName ||
            !state.lastName ||
            !!currentAdmins.find((a) => a.data.email === state.email)
          }
          onClick={async () => {
            return inviteUser(currentUser.userId, orgId, state).then(() => {
              enqueueSnackbarSuccess('Invitation created!');
              onClose();
            });
          }}
          pending="Inviting..."
        >
          Invite
        </ButtonWithPromise>
      </DialogActions>
    </Dialog>
  );
};

const Buttons = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: p.theme.spacing()
}));

export const AdminUserInvitations: React.FC<{
  org: Doc<IOrganization>;
}> = ({ org }) => {
  const [d, loading] = useMappedLoadingValue(
    combineLoadingValues(
      useAdminUsersInOrg(org.id),
      useAdminUserInvitationsInOrg(org.id)
    ),
    ([as, is]) => ({ adminUsers: as, invitations: is })
  );
  const inviteDialog = useDialogState(false);

  console.log('INIVTATIONS', d && d.invitations);

  return (
    <Section>
      <CanvasBar>
        <div>Admin User Invitations</div>
        <Buttons>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={inviteDialog.openDialog}
            disabled={!d}
          >
            Invite...
          </Button>
        </Buttons>
      </CanvasBar>
      {(loading || !d) && <PaperLoader height={HEIGHT} />}
      {!!d && (
        <RowsRendererScrollContainer height={HEIGHT}>
          <RowsRenderer
            variant="contained"
            columns={COLUMNS}
            rows={d.invitations}
            rowToKey={ID_TO_ROW_KEY}
            sorter={DEFAULT_SORTER}
            sortDirection={DEFAULT_SORTER.items.dir}
            renderHead={true}
            headProps={{ sticky: true }}
            chunkSize={30}
            rootMargin="300px"
            rowHeight={ROW_HEIGHTS.dense}
            otherProps={{ org }}
          />
        </RowsRendererScrollContainer>
      )}
      <InviteAdminUserDialog
        open={inviteDialog.dialogOpen}
        onClose={inviteDialog.closeDialog}
        orgId={org.id}
        currentAdmins={d ? d.adminUsers : EMPTY_ARR}
      />
    </Section>
  );
};
